/**
 * The files stage of a browser apply (item view): which created rows get
 * which File columns, handed to the server (POST /template/apply-files),
 * which streams the template's files into them. The browser never touches
 * the bytes and never names file ids: the server reads those off the
 * template by rowKey.
 *
 * A target: { rowKey, level, name, itemId, columnId, templateColumnId }
 *   rowKey            a subitem row's `id`, or a group row's `key`
 *   itemId            the monday item or subitem the browser just created
 *   columnId          the board's File column the cell was mapped to
 *   templateColumnId  the cell's column in the template (the server looks
 *                     the files up by it)
 */
import { canonicalType } from '../Config';
import { fileCellsIn } from './fileLimits';

/** Whether an apply of this template has any file to copy. */
export function templateHasFiles(template) {
  return fileCellsIn(template).some((files) => files.length > 0);
}

const fileCellsOf = (row) => (row && Array.isArray(row.column_values) ? row.column_values : []).filter((cv) => cv && canonicalType(String(cv.type || '')) === 'file' && Array.isArray(cv.files) && cv.files.length > 0);

/**
 * Targets for a row that exists, or failures for one that does not.
 * A cell whose column was skipped or unmapped is left out, like any other cell.
 */
function rowTargets(row, level, rowKey, id, idMap, skipIds, out, failures) {
  for (const cv of fileCellsOf(row)) {
    const templateColumnId = String(cv.id);
    if (skipIds && skipIds.has(templateColumnId)) continue;
    const columnId = idMap ? idMap[templateColumnId] : undefined;
    if (columnId === undefined) continue;
    if (!id) {
      failures.push({ level: 'file', name: row.name, reason: `skipped: its ${level} was not created` });
      continue;
    }
    out.push({ rowKey, level, name: row.name, itemId: String(id), columnId: String(columnId), templateColumnId });
  }
}

/**
 * Group apply: `created` is what applyGroupTemplate returns
 * ({ items: { key → id }, subitems: { key → id } }); `mapping` the same
 * { itemIdMap, itemSkipIds, subIdMap, subSkipIds } the run used.
 * @returns {{ targets: Array, failures: Array }}
 */
export function groupFileTargets(content, created, mapping, withSubitems = true) {
  const targets = [];
  const failures = [];
  const items = (content && content.items) || [];
  const c = created || { items: {}, subitems: {} };
  for (const it of items) {
    rowTargets(it, 'item', String(it.key), c.items[it.key], mapping.itemIdMap, mapping.itemSkipIds, targets, failures);
    if (!withSubitems) continue;
    for (const s of it.subitems || []) {
      rowTargets(s, 'subitem', String(s.key), c.subitems[s.key], mapping.subIdMap, mapping.subSkipIds, targets, failures);
    }
  }
  return { targets, failures };
}

/**
 * Subitem apply: `createdIds` maps a template row's id to the created
 * subitem's id (absent when the row failed).
 */
export function subitemFileTargets(subitems, createdIds, idMap, skipIds) {
  const targets = [];
  const failures = [];
  for (const s of subitems || []) {
    rowTargets(s, 'subitem', String(s.id), createdIds.get(s.id), idMap, skipIds, targets, failures);
  }
  return { targets, failures };
}

/** How many files the targets will copy, for the progress line. */
export function plannedFiles(template, targets) {
  const byRow = new Map();
  const rows = [];
  if (template && template.type === 'group') {
    for (const it of (template.content && template.content.items) || []) {
      rows.push({ key: String(it.key), row: it });
      for (const s of it.subitems || []) rows.push({ key: String(s.key), row: s });
    }
  } else {
    for (const s of (template && template.subitems) || []) rows.push({ key: String(s.id), row: s });
  }
  for (const { key, row } of rows) {
    const cells = new Map();
    for (const cv of fileCellsOf(row)) cells.set(String(cv.id), cv.files.length);
    byRow.set(key, cells);
  }
  let n = 0;
  for (const t of targets) n += (byRow.get(t.rowKey) && byRow.get(t.rowKey).get(t.templateColumnId)) || 0;
  return n;
}
